// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.0.4
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'
import api from '../api/client'

export default function VerifyEmail() {
  const { token } = useParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        await api.post('/api/portal/auth/verify-email', { token })
        setStatus('success')
        setMessage('Your email has been verified successfully!')
      } catch (error: any) {
        setStatus('error')
        setMessage(error.response?.data?.detail || 'Verification failed. The link may be expired.')
      }
    }

    if (token) {
      verifyEmail()
    }
  }, [token])

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        {status === 'loading' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500/20 rounded-2xl mb-4">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold">Verifying Email</h1>
            <p className="text-gray-400 mt-2">Please wait while we verify your email...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-2xl mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold">Email Verified!</h1>
            <p className="text-gray-400 mt-2">{message}</p>
            <Link to="/login" className="btn btn-primary mt-6">
              Continue to Login
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-2xl mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold">Verification Failed</h1>
            <p className="text-gray-400 mt-2">{message}</p>
            <div className="flex gap-4 justify-center mt-6">
              <Link to="/login" className="btn btn-secondary">
                Back to Login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
